import React, { useState, useEffect, useCallback } from 'react';
import { X, Plus, Search, Pencil, Power, PowerOff, Trash2, Download, Loader2, Info } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import CompanyModal from './CompanyModal';
import ImportSellersModal from './ImportSellersModal';
import { COMPANY_STATUS_FILTERS, formatInterval } from '../../utils/reconciliationUtils';
import { formatRecordDate } from '../../utils/dateUtils';

const CompanyManagerDrawer = ({ isOpen, onClose, onChanged, canDelete, defaultIntervalDays }) => {
    const [allCompanies, setAllCompanies] = useState([]);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [isLoading, setIsLoading] = useState(false);
    const [isCompanyModalOpen, setIsCompanyModalOpen] = useState(false);
    const [isImportOpen, setIsImportOpen] = useState(false);
    const [companyToEdit, setCompanyToEdit] = useState(null);
    const [dirty, setDirty] = useState(false);

    const fetchCompanies = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await api.get('/reconciliations/overview', { params: { includeInactive: 1 } });
            setAllCompanies(response.data.companies || []);
        } catch (error) {
            toast.error('Firma listesi yüklenemedi.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isOpen) {
            fetchCompanies();
        }
    }, [isOpen, fetchCompanies]);

    // Liste kucuk oldugu icin arama/durum filtresi istemcide yapilir
    const term = search.trim().toLocaleLowerCase('tr-TR');
    const companies = allCompanies
        .filter((company) => statusFilter === 'all' || company.status === statusFilter)
        .filter((company) => !term || company.name.toLocaleLowerCase('tr-TR').includes(term))
        .sort((a, b) => a.name.localeCompare(b.name, 'tr'));

    const markChanged = () => {
        setDirty(true);
        fetchCompanies();
    };

    const handleClose = () => {
        if (dirty && onChanged) {
            onChanged();
        }
        setDirty(false);
        onClose();
    };

    const handleDeactivate = async (company) => {
        if (!window.confirm(`"${company.name}" pasife alınsın mı?\n\nMutabakat listesinden ve uyarılardan çıkar. Geçmiş mutabakat kayıtları SİLİNMEZ; istediğiniz zaman yeniden aktifleştirebilirsiniz.`)) {
            return;
        }

        try {
            const response = await api.patch(`/reconciliations/companies/${company.id}/deactivate`);
            toast.success(response.data.message);
            markChanged();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Firma pasife alınamadı.');
        }
    };

    const handleActivate = async (company) => {
        try {
            await api.patch(`/reconciliations/companies/${company.id}/activate`, {});
            toast.success('Firma yeniden aktifleştirildi.');
            markChanged();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Firma aktifleştirilemedi.');
        }
    };

    const handleDelete = async (company) => {
        if (!window.confirm(`"${company.name}" firmasını silmek istediğinize emin misiniz?`)) {
            return;
        }

        try {
            await api.delete(`/reconciliations/companies/${company.id}`);
            toast.success('Firma silindi.');
            markChanged();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Firma silinemedi.');
        }
    };

    if (!isOpen) {
        return null;
    }

    return (
        <>
            <div className="fixed inset-0 z-[75] flex justify-end bg-black/60 backdrop-blur-sm" onClick={handleClose}>
                <div
                    className="flex h-full w-full max-w-2xl flex-col bg-dark-card border-l border-dark-border shadow-2xl"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="flex items-center justify-between border-b border-dark-border bg-dark-bg/40 p-5">
                        <div>
                            <h2 className="text-xl font-bold text-dark-text">Firma Defteri</h2>
                            <p className="text-sm text-dark-muted">Mutabakat yapılan firmalar</p>
                        </div>
                        <button onClick={handleClose} className="rounded-full p-2 text-dark-muted transition-colors hover:bg-dark-hover hover:text-dark-text">
                            <X size={20} />
                        </button>
                    </div>

                    <div className="border-b border-dark-border p-4 space-y-3">
                        <div className="flex flex-wrap gap-2">
                            <button
                                onClick={() => { setCompanyToEdit(null); setIsCompanyModalOpen(true); }}
                                className="flex items-center gap-2 rounded-xl bg-primary-600 px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-primary-500"
                            >
                                <Plus size={16} className="stroke-[3]" /> Yeni Firma
                            </button>
                            <button
                                onClick={() => setIsImportOpen(true)}
                                className="flex items-center gap-2 rounded-xl border border-dark-border bg-dark-bg/50 px-4 py-2 text-sm font-medium text-dark-muted transition-colors hover:text-dark-text"
                            >
                                <Download size={16} /> Tahsilatlardan İçe Aktar
                            </button>
                        </div>

                        <div className="flex flex-col gap-3 sm:flex-row">
                            <div className="relative flex-1">
                                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-dark-muted" />
                                <input
                                    type="text"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Firma ara..."
                                    className="w-full rounded-lg border border-dark-border bg-dark-bg py-2 pl-10 pr-4 text-dark-text focus:border-primary-500 focus:outline-none"
                                />
                            </div>
                            <div className="flex gap-1 rounded-lg border border-dark-border bg-dark-bg/50 p-1">
                                {COMPANY_STATUS_FILTERS.map((filter) => (
                                    <button
                                        key={filter.value}
                                        onClick={() => setStatusFilter(filter.value)}
                                        className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
                                            statusFilter === filter.value ? 'bg-primary-500/15 text-primary-500' : 'text-dark-muted hover:text-dark-text'
                                        }`}
                                    >
                                        {filter.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto">
                        {isLoading && (
                            <div className="flex items-center justify-center gap-2 p-10 text-dark-muted">
                                <Loader2 size={18} className="animate-spin" /> Yükleniyor...
                            </div>
                        )}

                        {!isLoading && companies.length === 0 && (
                            <div className="p-10 text-center text-dark-muted">
                                {allCompanies.length === 0 ? (
                                    <>
                                        Henüz firma eklenmemiş.
                                        <br />
                                        <span className="text-xs">"Yeni Firma" ile ekleyin veya tahsilatlardan içe aktarın.</span>
                                    </>
                                ) : 'Filtreye uyan firma bulunamadı.'}
                            </div>
                        )}

                        {!isLoading && companies.map((company) => (
                            <div key={company.id} className="flex items-start justify-between gap-3 border-b border-dark-border p-4 hover:bg-dark-hover transition-colors">
                                <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="font-semibold text-dark-text">{company.name}</span>
                                        <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
                                            company.status === 'aktif' ? 'tone-success' : 'border-transparent bg-dark-muted/20 text-dark-muted'
                                        }`}>
                                            {company.status === 'aktif' ? 'Aktif' : 'Pasif'}
                                        </span>
                                        <span className="rounded-md bg-dark-bg px-1.5 py-0.5 text-[11px] font-medium text-dark-muted">{company.defaultCurrency}</span>
                                    </div>
                                    <p className="mt-1 text-xs text-dark-muted">
                                        {company.lastEvent ? `Son mutabakat: ${formatRecordDate(company.lastEvent.reconciledDate)}` : 'Hiç mutabakat yapılmadı'}
                                        {` • ${formatInterval(company.intervalDays)}`}
                                        {company.taxNumber ? ` • VKN: ${company.taxNumber}` : ''}
                                    </p>
                                    {company.contactName && (
                                        <p className="text-xs text-dark-muted">{company.contactName}{company.contactPhone ? ` • ${company.contactPhone}` : ''}</p>
                                    )}
                                </div>

                                <div className="flex shrink-0 items-center gap-1">
                                    <button
                                        onClick={() => { setCompanyToEdit(company); setIsCompanyModalOpen(true); }}
                                        className="rounded-lg p-1.5 text-dark-muted transition-colors hover:bg-blue-500/10 hover:text-blue-500"
                                        title="Düzenle"
                                    >
                                        <Pencil size={16} />
                                    </button>
                                    {company.status === 'aktif' ? (
                                        <button
                                            onClick={() => handleDeactivate(company)}
                                            className="rounded-lg p-1.5 text-dark-muted transition-colors hover:bg-amber-500/10 hover:text-amber-500"
                                            title="Pasife al"
                                        >
                                            <PowerOff size={16} />
                                        </button>
                                    ) : (
                                        <button
                                            onClick={() => handleActivate(company)}
                                            className="rounded-lg p-1.5 text-dark-muted transition-colors hover:bg-emerald-500/10 hover:text-emerald-500"
                                            title="Yeniden aktifleştir"
                                        >
                                            <Power size={16} />
                                        </button>
                                    )}
                                    {canDelete && (
                                        <button
                                            onClick={() => handleDelete(company)}
                                            className="rounded-lg p-1.5 text-dark-muted transition-colors hover:bg-red-500/10 hover:text-red-500"
                                            title="Sil"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="flex items-start gap-2 border-t border-dark-border bg-dark-bg/40 p-4 text-xs text-dark-muted">
                        <Info size={14} className="mt-0.5 shrink-0" />
                        <span>Pasife alınan firma mutabakat listesinden ve uyarılardan çıkar; mutabakat geçmişi <strong className="text-dark-text">silinmez</strong>. Kaydı olan firma silinemez, yalnızca pasife alınır.</span>
                    </div>
                </div>
            </div>

            <CompanyModal
                isOpen={isCompanyModalOpen}
                onClose={() => setIsCompanyModalOpen(false)}
                onRefresh={markChanged}
                editData={companyToEdit}
                defaultIntervalDays={defaultIntervalDays}
            />

            <ImportSellersModal
                isOpen={isImportOpen}
                onClose={() => setIsImportOpen(false)}
                onRefresh={markChanged}
            />
        </>
    );
};

export default CompanyManagerDrawer;
